import type {
  AxiosBaseQueryArgs,
  AxiosBaseQueryError,
} from '@gasbi/shared/common/utils/axiosBaseQuery';
import type {
  BaseQueryFn,
  EndpointBuilder,
  EndpointDefinitions,
} from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

type SliceHeaders = Record<string, unknown>;

interface CrmSliceExtraOptions {
  /** The slice's url prefix, put in front of every endpoint url. */
  baseUrl?: string;
  /** Headers the slice sends with every request, under the request's own. */
  headers?: SliceHeaders;
}

const crmBaseQuery: BaseQueryFn<
  AxiosBaseQueryArgs,
  unknown,
  AxiosBaseQueryError,
  CrmSliceExtraOptions
> = (args, api, { baseUrl = '', headers } = {}) =>
  axiosBaseQuery({ baseUrl, headers })(args, api, {});

/**
 * The single RTK Query API of the CRM UI's own features (GSB-7899, the pattern of the BMS fix
 * GSB-7788). Each createApi adds a middleware, and on the first action every middleware dispatches
 * `middlewareRegistered` through the whole chain again, so the stack grows with the square of their
 * count: BMS overflowed it with 138 APIs, this app had 44 (33 own + 11 from @gasbi/shared). Features inject
 * their endpoints here with injectCrmSlice instead of calling createApi; the agents slice of
 * @gasbi/shared injects itself here too, through `initializeAgentsApi({ api: crmApi })`.
 */
export const crmApi = createApi({
  reducerPath: 'crmApi',
  baseQuery: crmBaseQuery,
  endpoints: () => ({}),
});

interface CrmSliceOptions<
  Definitions extends EndpointDefinitions,
  TagTypes extends string,
> {
  /** Url prefix of the slice's endpoints, the baseUrl its own createApi used to get. */
  baseUrl: string;
  /** Default headers of the slice's requests, the headers its own createApi's base query used to get. */
  headers?: SliceHeaders;
  tagTypes?: readonly TagTypes[];
  /** Default cache lifetime of the slice's queries; an endpoint's own value wins. */
  keepUnusedDataFor?: number;
  endpoints: (
    build: EndpointBuilder<
      typeof crmBaseQuery,
      TagTypes,
      typeof crmApi.reducerPath
    >,
  ) => Definitions;
}

/**
 * Injects a feature's endpoints into crmApi, taking the options its own createApi used to take.
 * Endpoint names share one namespace across the app, so a duplicate name throws at startup
 * instead of silently keeping the first definition.
 */
export const injectCrmSlice = <
  Definitions extends EndpointDefinitions,
  TagTypes extends string = never,
>({
  baseUrl,
  headers,
  tagTypes = [],
  keepUnusedDataFor,
  endpoints,
}: CrmSliceOptions<Definitions, TagTypes>) =>
  crmApi.enhanceEndpoints({ addTagTypes: tagTypes }).injectEndpoints({
    endpoints: build =>
      Object.fromEntries(
        Object.entries(endpoints(build)).map(([name, definition]) => [
          name,
          {
            keepUnusedDataFor,
            ...definition,
            extraOptions: {
              baseUrl,
              headers,
              ...(definition.extraOptions as CrmSliceExtraOptions | undefined),
            },
          },
        ]),
      ) as Definitions,
    overrideExisting: 'throw',
  });
